/**
 * ScheduleEditor Component
 * Full editor for a schedule - displays steps, allows reordering, adding, editing.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Skeleton,
  Alert,
  Snackbar,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSchedule } from '../../hooks/useSchedules';
import { useDevice } from '../../contexts/DeviceContext';
import { scheduleService } from '../../services/scheduleService';
import { actionsService } from '../../services/actionsService';
import { Action } from '../../types/action';
import { Schedule, ScheduleStep, ScheduleRecurrence, RoutineVisualization, SCHEDULE_VALIDATION, formatDuration, DayOfWeek } from '../../types/schedule';
import { useSchedules } from '../../hooks/useSchedules';
import { StepList } from './StepList';
import { StepEditor } from './StepEditor';
import { AddStepDialog } from './AddStepDialog';
import { EmojiPicker } from './EmojiPicker';
import { RecurrenceEditor } from './RecurrenceEditor';
import { FirstThenEditor } from './FirstThenEditor';
import { ChoiceBoardActiveNote, ChoiceBoardEditor, ChoiceBoardNotice } from './ChoiceBoardEditor';
import { RichText } from '../RichText';
import { WeeklyScheduleEditor } from './WeeklyScheduleEditor';

interface ScheduleEditorProps {
  scheduleId: string;
  onBack: () => void;
  /** This schedule is the active sent schedule: the tablet plays the copy it was sent (BA-92 m7) */
  isOnTablet?: boolean;
}

export const ScheduleEditor: React.FC<ScheduleEditorProps> = ({
  scheduleId,
  onBack,
  isOnTablet = false,
}) => {
  const { t } = useTranslation();
  const { selectedDevice } = useDevice();
  const deviceId = selectedDevice?.id ?? '';
  const { schedule, loading, error } = useSchedule(deviceId, scheduleId);
  const { schedules: allSchedules } = useSchedules(deviceId);  // For nested schedule selection

  // Local form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('📋');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Device actions for step picker
  const [deviceActions, setDeviceActions] = useState<Action[]>([]);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [actionsError, setActionsError] = useState(false); // BA-145: a failed load is not 'No Actions Yet'

  // Dialogs
  const [addStepDialogOpen, setAddStepDialogOpen] = useState(false);
  const [stepEditorOpen, setStepEditorOpen] = useState(false);
  const [stepToEdit, setStepToEdit] = useState<ScheduleStep | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteStepConfirmOpen, setDeleteStepConfirmOpen] = useState(false);
  const [stepToDelete, setStepToDelete] = useState<ScheduleStep | null>(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [unsavedWarningOpen, setUnsavedWarningOpen] = useState(false);
  const [recurrenceEditorOpen, setRecurrenceEditorOpen] = useState(false);
  const [firstThenPosition, setFirstThenPosition] = useState<'first' | 'then' | null>(null);
  const stepsHeadingRef = useRef<HTMLHeadingElement>(null);

  // Load device actions
  useEffect(() => {
    if (!deviceId) return;

    const loadActions = async () => {
      try {
        const response = await actionsService.listActions(deviceId);
        setDeviceActions(response.actions);
        setActionsError(false);
      } catch (err) {
        console.error('Failed to load actions:', err);
        setActionsError(true);
      } finally {
        setActionsLoading(false);
      }
    };

    loadActions();
  }, [deviceId]);

  // Sync local state with schedule data
  useEffect(() => {
    if (schedule) {
      setName(schedule.name);
      setDescription(schedule.description || '');
      setIcon(schedule.icon?.emoji || '📋');
      setHasChanges(false);
    }
  }, [schedule]);

  const handleNameChange = (value: string) => {
    setName(value);
    setHasChanges(true);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    setHasChanges(true);
  };

  const handleIconChange = (newIcon: string) => {
    setIcon(newIcon);
    setHasChanges(true);
  };

  const handleBack = () => {
    if (hasChanges) {
      setUnsavedWarningOpen(true);
    } else {
      onBack();
    }
  };

  const handleDiscardChanges = () => {
    setUnsavedWarningOpen(false);
    onBack();
  };

  const handleSave = async () => {
    if (!schedule) return;

    setSaving(true);
    setSaveError(null);

    try {
      await scheduleService.updateSchedule(deviceId, scheduleId, {
        name: name.trim(),
        description: description.trim() || undefined,
        icon: { type: 'emoji', emoji: icon },
      });
      setHasChanges(false);
      setSaveSuccess(true);
    } catch (err) {
      console.error('Failed to save schedule:', err);
      setSaveError(t('schedules.errors.failedToUpdateSchedule'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!schedule) return;

    try {
      await scheduleService.deleteSchedule(deviceId, scheduleId);
      onBack();
    } catch (err) {
      console.error('Failed to delete schedule:', err);
      setSaveError(t('schedules.errors.failedToDeleteSchedule'));
    }
    setDeleteConfirmOpen(false);
  };

  const handleAddStep = () => {
    setFirstThenPosition(null);
    setAddStepDialogOpen(true);
  };

  // For First-Then boards - add step at specific position
  const handleAddFirstThenStep = (position: 'first' | 'then') => {
    setFirstThenPosition(position);
    setAddStepDialogOpen(true);
  };

  const handleAddStepDialogClose = () => {
    setAddStepDialogOpen(false);
    setFirstThenPosition(null);
  };

  const handleActionsChange = async () => {
    // Reload actions when a new one is created
    try {
      const response = await actionsService.listActions(deviceId);
      setDeviceActions(response.actions);
      setActionsError(false);
    } catch (err) {
      console.error('Failed to reload actions:', err);
      setActionsError(true);
    }
  };

  const handleEditStep = (step: ScheduleStep) => {
    setStepToEdit(step);
    setFirstThenPosition(null);
    setStepEditorOpen(true);
  };

  const handleStepEditorClose = () => {
    setStepEditorOpen(false);
    setStepToEdit(null);
    setFirstThenPosition(null);
  };

  const handleDeleteStep = (step: ScheduleStep) => {
    setStepToDelete(step);
    setDeleteStepConfirmOpen(true);
  };

  const confirmDeleteStep = async () => {
    if (!stepToDelete) return;

    try {
      await scheduleService.deleteStep(deviceId, scheduleId, stepToDelete.id);
    } catch (err) {
      console.error('Failed to delete step:', err);
      setSaveError(t(schedule?.type === 'choice_board'
        ? 'schedules.choiceBoard.errors.removeFailed'
        : 'schedules.errors.failedToDeleteStep'));
    }
    // stepToDelete stays until the next delete, so the closing dialog keeps the choice's name
    setDeleteStepConfirmOpen(false);
  };

  const handleSaveRecurrence = async (recurrence: ScheduleRecurrence | null) => {
    if (!schedule) return;

    setSaving(true);
    setSaveError(null);

    try {
      await scheduleService.updateSchedule(deviceId, scheduleId, {
        recurrence,
      });
      setSaveSuccess(true);
    } catch (err) {
      console.error('Failed to save recurrence:', err);
      setSaveError(t('schedules.errors.failedToUpdateSchedule'));
    } finally {
      setSaving(false);
    }
  };

  // Format recurrence summary for display
  const formatRecurrenceSummary = (): string => {
    const recurrence = schedule?.recurrence;
    if (!recurrence || recurrence.type === 'manual') {
      return t('schedules.recurrence.manual');
    }

    const { type, startTime, daysOfWeek } = recurrence;
    let summary = '';

    switch (type) {
      case 'daily':
        summary = t('schedules.recurrence.daily');
        break;
      case 'weekdays':
        summary = t('schedules.recurrence.weekdays');
        break;
      case 'weekends':
        summary = t('schedules.recurrence.weekends');
        break;
      case 'weekly':
        if (daysOfWeek && daysOfWeek.length > 0) {
          const dayLabels = daysOfWeek.map((day: DayOfWeek) => t(`schedules.days.${day}`));
          summary = dayLabels.join(', ');
        } else {
          summary = t('schedules.recurrence.weekly');
        }
        break;
      case 'one_time':
        summary = t('schedules.recurrence.one_time');
        break;
      default:
        summary = t(`schedules.recurrence.${type}` as any);
    }

    if (startTime) {
      summary += ` @ ${startTime}`;
    }

    return summary;
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  if (error || !schedule) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={onBack} sx={{ mb: 2 }}>
          {t('common.cancel')}
        </Button>
        <Alert severity="error">
          {error?.message || 'Schedule not found'}
        </Alert>
      </Box>
    );
  }

  const steps = schedule.steps || [];
  const isChoiceBoard = schedule.type === 'choice_board';

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <IconButton onClick={handleBack}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">{schedule.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t(`schedules.types.${schedule.type}`)}
            {schedule.estimatedDurationSeconds != null && schedule.estimatedDurationSeconds > 0 && (
              <>
                {' '}&bull;{' '}
                {t('schedules.estimatedDuration', {
                  duration: formatDuration(schedule.estimatedDurationSeconds),
                })}
              </>
            )}
            {' '}&bull;{' '}
            {isChoiceBoard
              ? t('schedules.choiceBoard.choicesCount', { count: steps.length })
              : t('schedules.stepsCount', { count: steps.length })}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setDeleteConfirmOpen(true)}
          size="small"
        >
          {t('common.delete')}
        </Button>
      </Box>

      {/* BA-92: an older choice board over the cap, and a board the tablet is playing now */}
      {isChoiceBoard && (
        <ChoiceBoardNotice
          steps={steps}
          onShowChoices={() => stepsHeadingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        />
      )}
      {isChoiceBoard && isOnTablet && <ChoiceBoardActiveNote />}

      {/* Schedule details */}
      <Paper sx={{ p: 2, mb: 3 }}>
        {/* Icon picker */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Box
            onClick={() => setEmojiPickerOpen(true)}
            sx={{
              fontSize: '2.5rem',
              cursor: 'pointer',
              p: 1,
              borderRadius: 1,
              border: '2px dashed',
              borderColor: 'grey.300',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            {icon}
          </Box>
          <TextField
            fullWidth
            label={t('schedules.fields.name')}
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            inputProps={{ maxLength: SCHEDULE_VALIDATION.NAME_MAX_LENGTH }}
          />
        </Box>
        <TextField
          fullWidth
          label={t('schedules.fields.description')}
          value={description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          multiline
          rows={2}
          inputProps={{ maxLength: SCHEDULE_VALIDATION.DESCRIPTION_MAX_LENGTH }}
        />
        {hasChanges && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || !name.trim()}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </Box>
        )}
      </Paper>

      {/* For routines: Visualization Mode selector */}
      {/* For other types: Recurrence section */}
      {schedule.type === 'routine' ? (
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('schedules.routineVisualization.title')}</InputLabel>
            <Select
              value={schedule.settings?.routineVisualization || 'overview_then_fullscreen'}
              label={t('schedules.routineVisualization.title')}
              onChange={async (e) => {
                const newVisualization = e.target.value as RoutineVisualization;
                try {
                  await scheduleService.updateSchedule(deviceId, scheduleId, {
                    settings: {
                      ...schedule.settings,
                      routineVisualization: newVisualization,
                    },
                  });
                  setSaveSuccess(true);
                } catch (err) {
                  console.error('Failed to update visualization:', err);
                  setSaveError(t('schedules.errors.failedToUpdateSchedule'));
                }
              }}
            >
              <MenuItem value="overview_only">
                <Box>
                  <Typography variant="body2">{t('schedules.routineVisualization.overview_only')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('schedules.routineVisualization.overview_only_desc')}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="overview_then_fullscreen">
                <Box>
                  <Typography variant="body2">{t('schedules.routineVisualization.overview_then_fullscreen')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('schedules.routineVisualization.overview_then_fullscreen_desc')}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="fullscreen_only">
                <Box>
                  <Typography variant="body2">{t('schedules.routineVisualization.fullscreen_only')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('schedules.routineVisualization.fullscreen_only_desc')}
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Paper>
      ) : (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {t('schedules.recurrence.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatRecurrenceSummary()}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ScheduleIcon />}
              onClick={() => setRecurrenceEditorOpen(true)}
              size="small"
            >
              {t('common.edit')}
            </Button>
          </Box>
        </Paper>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Steps section - render appropriate editor based on schedule type */}
      {schedule.type !== 'weekly_schedule' && (
        <Typography variant="h6" sx={{ mb: 2 }} ref={stepsHeadingRef}>
          {isChoiceBoard ? t('schedules.choiceBoard.title') : t('schedules.steps.title')}
        </Typography>
      )}

      {schedule.type === 'weekly_schedule' ? (
        <WeeklyScheduleEditor
          schedule={schedule}
          deviceId={deviceId}
          deviceActions={deviceActions}
          deviceSchedules={allSchedules}
          onScheduleUpdated={() => {/* Firebase listener will handle refresh */}}
          onActionsChange={handleActionsChange}
        />
      ) : schedule.type === 'first_then' ? (
        <FirstThenEditor
          steps={steps}
          deviceActions={deviceActions}
          onAddStep={handleAddFirstThenStep}
          onEditStep={handleEditStep}
          onDeleteStep={handleDeleteStep}
        />
      ) : schedule.type === 'choice_board' ? (
        <ChoiceBoardEditor
          steps={steps}
          deviceActions={deviceActions}
          deviceId={deviceId}
          scheduleId={scheduleId}
          onAddStep={handleAddStep}
          onEditStep={handleEditStep}
          onDeleteStep={handleDeleteStep}
          onReorderError={() => setSaveError(t('schedules.choiceBoard.errors.moveFailed'))}
        />
      ) : (
        <StepList
          steps={steps}
          deviceId={deviceId}
          scheduleId={scheduleId}
          deviceActions={deviceActions}
          deviceSchedules={allSchedules}
          onAddStep={handleAddStep}
          onEditStep={handleEditStep}
          onDeleteStep={handleDeleteStep}
        />
      )}

      {/* Add step dialog */}
      <AddStepDialog
        open={addStepDialogOpen}
        onClose={handleAddStepDialogClose}
        onAdded={handleAddStepDialogClose}
        deviceId={deviceId}
        scheduleId={scheduleId}
        currentScheduleId={scheduleId}
        scheduleType={schedule.type}
        deviceActions={deviceActions}
        actionsError={actionsError}
        deviceSchedules={allSchedules}
        defaultOrder={firstThenPosition === 'first' ? 0 : firstThenPosition === 'then' ? 1 : undefined}
        onActionsChange={handleActionsChange}
      />

      {/* Step editor dialog (edit only) */}
      {stepToEdit && (
        <StepEditor
          open={stepEditorOpen}
          onClose={handleStepEditorClose}
          onSaved={handleStepEditorClose}
          step={stepToEdit}
          deviceId={deviceId}
          scheduleId={scheduleId}
          deviceActions={deviceActions}
          deviceSchedules={allSchedules}
          scheduleType={schedule.type}
        />
      )}

      {/* Delete schedule confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('schedules.deleteConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('schedules.deleteConfirm.message', { name: schedule.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete step confirmation */}
      <Dialog open={deleteStepConfirmOpen} onClose={() => setDeleteStepConfirmOpen(false)}>
        <DialogTitle>
          {isChoiceBoard ? t('schedules.choiceBoard.removeConfirm.title') : t('schedules.deleteStepConfirm.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isChoiceBoard
              ? <RichText i18nKey="schedules.choiceBoard.removeConfirm.message" values={{ title: stepToDelete?.title ?? '' }} />
              : t('schedules.deleteStepConfirm.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteStepConfirmOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDeleteStep} color="error" variant="contained">
            {isChoiceBoard ? t('schedules.choiceBoard.removeConfirm.confirm') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/error snackbars */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
      >
        <Alert severity="success">{t('schedules.success.scheduleUpdated')}</Alert>
      </Snackbar>
      <Snackbar
        open={!!saveError}
        autoHideDuration={4000}
        onClose={() => setSaveError(null)}
      >
        <Alert severity="error">{saveError}</Alert>
      </Snackbar>

      {/* Emoji picker */}
      <EmojiPicker
        open={emojiPickerOpen}
        onClose={() => setEmojiPickerOpen(false)}
        onSelect={handleIconChange}
        currentEmoji={icon}
      />

      {/* Unsaved changes warning */}
      <Dialog open={unsavedWarningOpen} onClose={() => setUnsavedWarningOpen(false)}>
        <DialogTitle>{t('schedules.unsavedChanges.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('schedules.unsavedChanges.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnsavedWarningOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDiscardChanges} color="error">
            {t('schedules.unsavedChanges.discard')}
          </Button>
          <Button
            onClick={async () => {
              await handleSave();
              setUnsavedWarningOpen(false);
              onBack();
            }}
            variant="contained"
            disabled={saving}
          >
            {t('schedules.unsavedChanges.saveAndExit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recurrence editor */}
      <RecurrenceEditor
        open={recurrenceEditorOpen}
        onClose={() => setRecurrenceEditorOpen(false)}
        onSave={handleSaveRecurrence}
        recurrence={schedule?.recurrence}
      />
    </Box>
  );
};
